/**
 * 키워드 조회 이력 — <b>브라우저에만</b> 남긴다.
 *
 * <p><b>왜 서버 DB 가 아닌가</b> — 결과에는 발행량·포화도가 들어 있고, 그건 네이버 검색 오픈API 에서
 * 온 값이다. 오픈API 약관은 검색 결과의 복사·저장·캐싱을 금지한다. 서버에 쌓아 두면 그대로 걸린다.
 * 브라우저 localStorage 는 <b>이용자 본인 기기</b>에 남는 화면 상태에 가깝고 서버에는 아무것도
 * 남지 않으므로, 요구(다시 보기·호출 절약)를 채우면서 선을 넘지 않는 선택이다.
 *
 * <p>그래서 기기가 바뀌면 이력도 따라오지 않는다 — 그게 이 구조의 대가다.
 */

const KEY = "keyword.history.v1";
const ACTIVE_KEY = "keyword.history.active.v1";

/** 보관 개수. 넘으면 오래된 것부터 <b>결과째</b> 사라진다 */
export const MAX_HISTORY = 10;

export interface HistoryEntry {
  id: string;
  seed: string;
  /** 저장 시각 (epoch ms) */
  at: number;
  profile: unknown;
  insight: unknown;
  result: unknown;
  /** 아직 조회가 끝나지 않았는가. 목록에 "조회 중"으로 보여 준다 */
  pending?: boolean;
}

function read(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function write(list: HistoryEntry[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch (e) {
    // 용량 초과(QuotaExceeded)면 절반으로 줄여 한 번 더 시도한다.
    // 그래도 안 되면 이력을 포기한다 — 이력 때문에 조회가 막히면 안 된다.
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, Math.floor(list.length / 2))));
    } catch (e2) {
      /* 포기 */
    }
  }
}

export function list(): HistoryEntry[] {
  return read().sort((a, b) => b.at - a.at);
}

/**
 * <b>조회를 시작할 때</b> 자리를 먼저 만든다.
 *
 * <p>예전에는 세 조회가 다 끝난 뒤에야 이력에 넣었다. 그래서 15~25초 동안 목록에 아무것도 없었고,
 * 그 사이 다른 메뉴로 갔다 오면 진행 중이던 조회가 통째로 사라졌다(사용자 지적).
 * 이제 빈 자리를 먼저 잡고 {@link update} 로 결과가 오는 대로 채운다 —
 * 중간에 화면을 떠나도 <b>거기까지 받은 것</b>은 남는다.
 *
 * <p>같은 키워드가 이미 있으면 덮어쓰고 맨 앞으로 올린다
 * (같은 말을 여러 번 찾았다고 목록이 그걸로 채워지면 안 된다).
 */
export function begin(seed: string): HistoryEntry {
  const entry: HistoryEntry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    seed,
    at: Date.now(),
    profile: null,
    insight: null,
    result: null,
    pending: true,
  };
  const kept = read().filter((e) => e.seed !== seed);
  kept.unshift(entry);
  write(kept.slice(0, MAX_HISTORY));   // 10개를 넘기면 오래된 것이 결과째 버려진다
  setActive(entry.id);
  return entry;
}

/** 받은 것만 채워 넣는다. 없는 항목(이미 지웠다면)은 조용히 무시한다. */
export function update(id: string, fields: Partial<HistoryEntry>): HistoryEntry | null {
  const list = read();
  const idx = list.findIndex((e) => e.id === id);
  if (idx < 0) {
    return null;
  }
  const merged = { ...list[idx], ...fields };
  list[idx] = merged;
  write(list);
  return merged;
}

/** 한 건 삭제 — 결과도 함께 사라진다(같은 항목 안에 들어 있다) */
export function remove(id: string): HistoryEntry[] {
  const kept = read().filter((e) => e.id !== id);
  write(kept);
  if (getActive() === id) {
    setActive(kept.length ? kept[0].id : null);
  }
  return kept.sort((a, b) => b.at - a.at);
}

export function clear() {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(ACTIVE_KEY);
  } catch (e) {
    /* 무시 */
  }
}

export function find(id: string): HistoryEntry | null {
  return read().find((e) => e.id === id) || null;
}

/** 마지막으로 보고 있던 항목. 화면을 떠났다 돌아와도 그대로 복원하려고 둔다 */
export function getActive(): string | null {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch (e) {
    return null;
  }
}

export function setActive(id: string | null) {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch (e) {
    /* 무시 */
  }
}

/** 목록에 보여줄 상대 시각 */
export function relTime(at: number): string {
  const m = Math.floor((Date.now() - at) / 60000);
  if (m < 1) return "방금";
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  return `${Math.floor(h / 24)}일 전`;
}
